import { DateTime } from "luxon";

/**
 * The repeat picker, in plain language.
 *
 * Spec §8 and the design are emphatic: no RRULE string is ever shown to a
 * human. This module is the only place the two vocabularies meet — everything
 * above it talks about "every week on Tuesday and Thursday", everything below
 * it talks RFC 5545.
 */

export type RepeatKind = "none" | "daily" | "weekly" | "monthly" | "yearly";

export interface Repeat {
  kind: RepeatKind;
  /** 0 = Sunday, for weekly. */
  byDay: number[];
}

const RRULE_DAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function toRrule(repeat: Repeat, startDate: string, zone: string): string | null {
  const start = DateTime.fromISO(startDate, { zone });

  switch (repeat.kind) {
    case "none":
      return null;
    case "daily":
      return "FREQ=DAILY";
    case "weekly": {
      const days = repeat.byDay.length > 0 ? repeat.byDay : [start.weekday % 7];
      return `FREQ=WEEKLY;BYDAY=${days
        .slice()
        .sort((a, b) => a - b)
        .map((d) => RRULE_DAYS[d])
        .join(",")}`;
    }
    case "monthly":
      return `FREQ=MONTHLY;BYMONTHDAY=${String(start.day)}`;
    case "yearly":
      return "FREQ=YEARLY";
  }
}

export function fromRrule(rrule: string | null): Repeat {
  if (rrule === null || rrule.trim() === "") return { kind: "none", byDay: [] };

  const upper = rrule.toUpperCase();

  if (upper.includes("FREQ=DAILY")) return { kind: "daily", byDay: [] };
  if (upper.includes("FREQ=MONTHLY")) return { kind: "monthly", byDay: [] };
  if (upper.includes("FREQ=YEARLY")) return { kind: "yearly", byDay: [] };

  if (upper.includes("FREQ=WEEKLY")) {
    const match = /BYDAY=([^;]+)/.exec(upper);
    const byDay =
      match === null
        ? []
        : match[1]!
            .split(",")
            .map((token) => RRULE_DAYS.indexOf(token.trim()))
            .filter((index) => index >= 0);
    return { kind: "weekly", byDay };
  }

  return { kind: "none", byDay: [] };
}

/** What the row in the editor reads. */
export function describe(repeat: Repeat, startDate: string, zone: string): string {
  const start = DateTime.fromISO(startDate, { zone });

  switch (repeat.kind) {
    case "none":
      return "Doesn't repeat";
    case "daily":
      return "Every day";
    case "weekly": {
      const days = (repeat.byDay.length > 0 ? repeat.byDay : [start.weekday % 7])
        .slice()
        .sort((a, b) => a - b);
      if (days.length === 5 && days.join() === "1,2,3,4,5") return "Every weekday";
      if (days.length === 7) return "Every day";
      return `Weekly on ${days.map((d) => NAMES[d]).join(", ")}`;
    }
    case "monthly":
      return `Monthly on the ${String(start.day)}`;
    case "yearly":
      return `Every year on ${start.toFormat("LLL d")}`;
  }
}

/** The longer sentence under the picker, which explains the consequence. */
export function summarise(repeat: Repeat, startDate: string, zone: string): string {
  if (repeat.kind === "none") return "It happens once.";
  const start = DateTime.fromISO(startDate, { zone });
  return `${describe(repeat, startDate, zone)}, starting ${start.toFormat("ccc d LLL")}.`;
}

export const WEEKDAY_LABELS = NAMES;
